import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/lib/auth-context";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Loader2,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Clock,
  Globe,
  Plug,
  Database,
  Mail,
  Accessibility,
} from "lucide-react";
import { toast } from "sonner";
import { api, API_BASE_URL, tokenStore } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export const Route = createFileRoute("/login")({ component: LoginPage });

type CheckStatus = "pending" | "ok" | "error";
interface DiagItem {
  label: string;
  status: CheckStatus;
  detail?: string;
}

interface HealthCheck {
  ok?: boolean;
  detail?: string;
  error?: string;
  [k: string]: unknown;
}
interface HealthReport {
  status?: string;
  checks?: Record<string, HealthCheck | Record<string, boolean>>;
}

// Mode dev : Vite, ou ?diag=1 dans l'URL pour activer le panneau détaillé en prod (mode maintenance).
const IS_DEV_OR_MAINTENANCE =
  import.meta.env.DEV ||
  (typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).has("diag"));

function StatusIcon({ status }: { status: CheckStatus }) {
  if (status === "ok") return <CheckCircle2 className="h-4 w-4 text-green-600" />;
  if (status === "error") return <XCircle className="h-4 w-4 text-destructive" />;
  return <Clock className="h-4 w-4 text-muted-foreground animate-pulse" />;
}

function ServiceBadge({
  icon: Icon,
  label,
  status,
  title,
}: {
  icon: typeof Globe;
  label: string;
  status: CheckStatus;
  title?: string;
}) {
  const color =
    status === "ok"
      ? "text-green-600 border-green-600/30 bg-green-600/5"
      : status === "error"
        ? "text-destructive border-destructive/30 bg-destructive/5"
        : "text-muted-foreground border-muted-foreground/30 bg-muted/30";
  return (
    <div
      title={title}
      className={`flex flex-col items-center gap-1 rounded-lg border px-2 py-2 ${color}`}
    >
      <div className="relative">
        <Icon className="h-5 w-5" />
        <span className="absolute -bottom-1 -right-1 flex h-3 w-3 items-center justify-center rounded-full bg-background">
          {status === "ok" ? (
            <CheckCircle2 className="h-3 w-3 text-green-600" />
          ) : status === "error" ? (
            <XCircle className="h-3 w-3 text-destructive" />
          ) : (
            <Clock className="h-3 w-3 text-muted-foreground animate-pulse" />
          )}
        </span>
      </div>
      <span className="text-[11px] font-medium">{label}</span>
    </div>
  );
}

function LoginPage() {
  const { user, signIn, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [diagOpen, setDiagOpen] = useState(false);
  const [bootStart] = useState(() => Date.now());
  const [now, setNow] = useState(() => Date.now());
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);

  const [apiCheck, setApiCheck] = useState<CheckStatus>("pending");
  const [apiDetail, setApiDetail] = useState<string>("");
  const [healthReport, setHealthReport] = useState<HealthReport | null>(null);
  const [hydrated, setHydrated] = useState(false);
  const [assets, setAssets] = useState<{ js: number; css: number }>({ js: 0, css: 0 });
  const [orgName, setOrgName] = useState<string>("");

  // Mode accessibilité (police plus grande + fort contraste). Persisté en localStorage,
  // appliqué sur <html> via la classe `a11y` pour qu'il survive après connexion.
  const [a11y, setA11y] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const enabled = localStorage.getItem("a11y-mode") === "1";
    setA11y(enabled);
    document.documentElement.classList.toggle("a11y", enabled);
  }, []);
  const toggleA11y = () => {
    const next = !a11y;
    setA11y(next);
    if (typeof window !== "undefined") {
      localStorage.setItem("a11y-mode", next ? "1" : "0");
      document.documentElement.classList.toggle("a11y", next);
    }
  };


  // Marqueur d'hydratation React : si ce useEffect s'exécute, React tourne côté client.
  useEffect(() => {
    setHydrated(true);
    if (typeof document !== "undefined") {
      const js = document.querySelectorAll('script[src*="/assets/"]').length;
      const css = document.querySelectorAll('link[rel="stylesheet"][href*="/assets/"]').length;
      setAssets({ js, css });
    }
  }, []);

  useEffect(() => {
    if (user) navigate({ to: "/" });
  }, [user, navigate]);

  // Ouvre auto le diagnostic détaillé si auth bloquée après 3s (uniquement en dev/maintenance)
  useEffect(() => {
    if (!IS_DEV_OR_MAINTENANCE) return;
    const t = setTimeout(() => {
      if (authLoading) setDiagOpen(true);
    }, 3000);
    return () => clearTimeout(t);
  }, [authLoading]);

  // Compteur d'écoulé : 1 Hz seulement, et uniquement si panneau ouvert
  useEffect(() => {
    if (!diagOpen) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [diagOpen]);

  const runApiCheck = async () => {
    setApiCheck("pending");
    setApiDetail("");
    setHealthReport(null);
    try {
      const ctrl = new AbortController();
      // Timeout court (3s) pour ne pas geler la page si l'API ne répond pas.
      const timeout = setTimeout(() => ctrl.abort(), 3000);
      const res = await fetch(`${API_BASE_URL}/api/health`, {
        signal: ctrl.signal,
        cache: "no-store",
        headers: { Accept: "application/json" },
      });
      clearTimeout(timeout);
      const ct = res.headers.get("content-type") || "";
      if (!res.ok) {
        setApiCheck("error");
        setApiDetail(
          `HTTP ${res.status} — ${
            ct.includes("html")
              ? "le backend a renvoyé une page HTML (souvent une erreur serveur LWS)"
              : res.statusText || "erreur backend"
          }`,
        );
        return;
      }
      if (!ct.includes("json")) {
        setApiCheck("error");
        setApiDetail(`Réponse non-JSON (content-type: ${ct || "inconnu"})`);
        return;
      }
      const json = (await res.json()) as HealthReport;
      setHealthReport(json);
      setApiCheck("ok");
      setApiDetail(`${res.status} ${res.statusText || "OK"}`);
    } catch (e) {
      setApiCheck("error");
      const msg = e instanceof Error ? e.message : "Erreur inconnue";
      setApiDetail(
        msg.includes("abort")
          ? "Timeout (>3s) — le backend ne répond pas"
          : msg,
      );
    }
  };

  // Lance toujours le health check une fois au montage (pour alimenter les 4 badges).
  const [apiCheckTriggered, setApiCheckTriggered] = useState(false);
  useEffect(() => {
    if (!apiCheckTriggered) {
      setApiCheckTriggered(true);
      runApiCheck();
    }
  }, [apiCheckTriggered]);

  // Récupère le nom de l'association (endpoint public, sans auth).
  useEffect(() => {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 3000);
    fetch(`${API_BASE_URL}/api/org-settings/public`, {
      signal: ctrl.signal,
      cache: "no-store",
      headers: { Accept: "application/json" },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((j: { data?: { name?: string | null } } | null) => {
        if (j?.data?.name) setOrgName(j.data.name);
      })
      .catch(() => {
        /* fallback silencieux */
      })
      .finally(() => clearTimeout(t));
    return () => {
      ctrl.abort();
      clearTimeout(t);
    };
  }, []);


  const handle = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const email = String(form.get("email") ?? "").trim();
    const password = String(form.get("password") ?? "");
    setLoading(true);
    const { error } = await signIn(email, password);
    setLoading(false);
    if (error) toast.error(error);
  };

  const handleReload = () => {
    if ("caches" in window) {
      caches.keys().then((keys) => Promise.all(keys.map((k) => caches.delete(k))));
    }
    window.location.reload();
  };

  const handleForgotSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const email = forgotEmail.trim();
    if (!email) return;
    setForgotLoading(true);
    try {
      await api.post("/api/auth/password-reset/request", {
        email,
        reset_url_base: window.location.origin,
      });
      toast.success(
        "Si un compte existe pour cette adresse, un email de réinitialisation vient d'être envoyé.",
      );
      setForgotOpen(false);
      setForgotEmail("");
    } catch {
      toast.error("Impossible d'envoyer la demande. Réessayez plus tard.");
    } finally {
      setForgotLoading(false);
    }
  };

  const handleHardReset = () => {
    try {
      tokenStore.clear();
      sessionStorage.clear();
      localStorage.clear();
    } catch {
      // ignore
    }
    window.location.href = "/login";
  };

  const diagnostics = useMemo<DiagItem[]>(() => {
    const items: DiagItem[] = [];

    items.push({
      label: "Hydratation React",
      status: hydrated ? "ok" : "error",
      detail: hydrated
        ? "React s'est exécuté côté client"
        : "React ne s'est pas hydraté (page figée)",
    });

    items.push({
      label: "Bundle JavaScript",
      status: assets.js > 0 ? "ok" : "error",
      detail:
        assets.js > 0
          ? `${assets.js} script(s) /assets/*.js chargé(s)`
          : "Aucun <script src='/assets/*.js'> trouvé dans le HTML — index.html mal généré ou mal uploadé",
    });

    items.push({
      label: "Feuille de style",
      status: assets.css > 0 ? "ok" : "error",
      detail:
        assets.css > 0
          ? `${assets.css} CSS /assets/*.css chargé(s)`
          : "Aucun <link rel='stylesheet' href='/assets/*.css'> trouvé — styles manquants",
    });

    items.push({
      label: "Configuration API",
      status: API_BASE_URL ? "ok" : "error",
      detail: API_BASE_URL || "VITE_API_BASE_URL non défini",
    });

    items.push({
      label: "Connectivité backend",
      status: apiCheck,
      detail: apiDetail || (apiCheck === "pending" ? "Test en cours…" : ""),
    });

    const token = tokenStore.get();
    const maskedToken = token
      ? `${token.slice(0, 8)}…${token.slice(-4)} (${token.length} car.)`
      : "—";
    items.push({
      label: "URL /api/auth/me",
      status: "ok",
      detail: `${API_BASE_URL}/api/auth/me\nAuthorization: Bearer ${maskedToken}`,
    });

    items.push({
      label: "Initialisation auth",
      status: authLoading ? "pending" : "ok",
      detail: authLoading
        ? "Vérification du token en cours…"
        : token
          ? "Token présent"
          : "Aucun token (normal si non connecté)",
    });

    let storageOk = false;
    try {
      sessionStorage.setItem("__diag", "1");
      sessionStorage.removeItem("__diag");
      storageOk = true;
    } catch {
      storageOk = false;
    }
    items.push({
      label: "Stockage navigateur",
      status: storageOk ? "ok" : "error",
      detail: storageOk
        ? "sessionStorage disponible"
        : "sessionStorage bloqué (mode privé ?)",
    });

    items.push({
      label: "Connexion réseau",
      status: typeof navigator !== "undefined" && navigator.onLine ? "ok" : "error",
      detail:
        typeof navigator !== "undefined" && navigator.onLine ? "En ligne" : "Hors ligne",
    });

    return items;
  }, [apiCheck, apiDetail, authLoading, hydrated, assets]);

  const elapsed = Math.round((now - bootStart) / 100) / 10;
  const hasError = diagnostics.some((d) => d.status === "error");
  const stillPending = diagnostics.some((d) => d.status === "pending");
  const okCount = diagnostics.filter((d) => d.status === "ok").length;

  // === 4 statuts haut niveau (toujours affichés) ===
  // 1. Application : React s'est hydraté + assets chargés
  const appStatus: CheckStatus =
    hydrated && assets.js > 0 && assets.css > 0 ? "ok" : hydrated ? "error" : "pending";

  // 2. API : connectivité au backend
  const apiStatus: CheckStatus = apiCheck;

  // 3. BDD : extrait du rapport health (db_query.ok)
  const dbCheck = healthReport?.checks?.db_query as HealthCheck | undefined;
  const dbStatus: CheckStatus =
    apiCheck === "pending"
      ? "pending"
      : apiCheck === "error"
        ? "error"
        : dbCheck?.ok === true
          ? "ok"
          : "error";

  // 4. Mail : extrait du rapport health (smtp_configured.ok)
  const mailCheck = healthReport?.checks?.smtp_configured as HealthCheck | undefined;
  const mailStatus: CheckStatus =
    apiCheck === "pending"
      ? "pending"
      : apiCheck === "error"
        ? "error"
        : mailCheck === undefined
          ? "pending"
          : mailCheck.ok === true
            ? "ok"
            : "error";

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary via-primary to-sidebar px-4 py-8">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-8 relative">
          <button
            type="button"
            onClick={toggleA11y}
            aria-pressed={a11y}
            title={
              a11y
                ? "Désactiver le mode accessibilité"
                : "Activer le mode accessibilité (police plus grande, fort contraste)"
            }
            className="absolute right-0 top-0 flex items-center gap-1 rounded-md border border-primary-foreground/30 bg-primary-foreground/10 px-2 py-1 text-xs text-primary-foreground hover:bg-primary-foreground/20 transition-colors"
          >
            <Accessibility className="h-4 w-4" />
            <span className="hidden sm:inline">{a11y ? "Mode normal" : "Accessibilité"}</span>
          </button>
          <div
            aria-hidden="true"
            className="flex h-14 w-14 items-center justify-center rounded-xl bg-accent text-accent-foreground shadow-lg font-display font-bold text-xl tracking-tight"
          >
            GA
          </div>
          <h1 className="mt-4 font-display text-3xl font-semibold text-primary-foreground">
            Gest'Asso 1.0
          </h1>
          <p className="mt-1 text-sm text-primary-foreground/70">
            {orgName || "Espace réservé"}
          </p>
        </div>

        <Card className="p-6">
          <form onSubmit={handle} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                required
                autoComplete="email"
                inputMode="email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Mot de passe</Label>
              <Input
                id="password"
                name="password"
                type="password"
                required
                minLength={6}
                autoComplete="current-password"
              />
            </div>
            <Button type="submit" className="w-full" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Se connecter
            </Button>
            <div className="text-center">
              <button
                type="button"
                onClick={() => setForgotOpen(true)}
                className="text-xs text-primary hover:underline"
              >
                J'ai oublié mon mot de passe
              </button>
            </div>
            <p className="text-xs text-muted-foreground pt-2">
              Les comptes sont créés par un administrateur. Contactez le bureau si vous n'avez
              pas d'accès.
            </p>
          </form>
        </Card>

        <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Réinitialiser votre mot de passe</DialogTitle>
              <DialogDescription>
                Saisissez votre adresse email. Si un compte existe, vous recevrez un lien
                sécurisé pour définir un nouveau mot de passe.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleForgotSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="forgot-email">Email</Label>
                <Input
                  id="forgot-email"
                  type="email"
                  required
                  autoComplete="email"
                  inputMode="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                />
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setForgotOpen(false)}
                  disabled={forgotLoading}
                >
                  Annuler
                </Button>
                <Button type="submit" disabled={forgotLoading || !forgotEmail.trim()}>
                  {forgotLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Envoyer le lien
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* 4 badges de statut services — TOUJOURS visibles */}
        <Card className="mt-4 p-3">
          <div className="grid grid-cols-4 gap-2">
            <ServiceBadge
              icon={Globe}
              label="Application"
              status={appStatus}
              title="État du chargement de l'application web"
            />
            <ServiceBadge
              icon={Plug}
              label="API"
              status={apiStatus}
              title={apiDetail || "Connectivité au backend"}
            />
            <ServiceBadge
              icon={Database}
              label="BDD"
              status={dbStatus}
              title={dbCheck?.detail || (dbCheck?.ok ? "Base accessible" : "Base inaccessible")}
            />
            <ServiceBadge
              icon={Mail}
              label="Mail"
              status={mailStatus}
              title={mailCheck?.detail || "Configuration SMTP"}
            />
          </div>
        </Card>

        {/* Panneau de diagnostic détaillé — UNIQUEMENT en mode dev/maintenance */}
        {IS_DEV_OR_MAINTENANCE && (
          <Card className="mt-4 overflow-hidden">
            <button
              type="button"
              onClick={() => setDiagOpen((v) => !v)}
              className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium hover:bg-muted/50 transition-colors"
              aria-expanded={diagOpen}
            >
              <span className="flex items-center gap-2">
                {hasError ? (
                  <XCircle className="h-4 w-4 text-destructive" />
                ) : stillPending ? (
                  <Clock className="h-4 w-4 text-muted-foreground animate-pulse" />
                ) : (
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                )}
                Diagnostic détaillé{" "}
                <span className="text-muted-foreground font-normal">
                  ({okCount}/{diagnostics.length})
                </span>
              </span>
              {diagOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>

            {diagOpen && (
              <div className="border-t px-4 py-3 space-y-3 bg-muted/20">
                <ul className="space-y-2 text-sm">
                  {diagnostics.map((d, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="mt-0.5 flex-shrink-0">
                        <StatusIcon status={d.status} />
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium">{d.label}</div>
                        {d.detail && (
                          <div className="text-xs text-muted-foreground break-all whitespace-pre-line font-mono">
                            {d.detail}
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>

                <div className="text-xs text-muted-foreground pt-2 border-t">
                  Temps écoulé depuis l'ouverture : <strong>{elapsed.toFixed(1)}s</strong>
                </div>

                <div className="flex flex-col gap-2 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={runApiCheck}
                    className="w-full"
                  >
                    <RefreshCw className="mr-2 h-3 w-3" />
                    Re-tester le backend
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleReload}
                    className="w-full"
                  >
                    <RefreshCw className="mr-2 h-3 w-3" />
                    Recharger (vider cache)
                  </Button>
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    onClick={handleHardReset}
                    className="w-full"
                  >
                    Réinitialiser session & recharger
                  </Button>
                </div>

                <div className="text-[10px] text-muted-foreground pt-2 border-t font-mono break-all">
                  UA: {typeof navigator !== "undefined" ? navigator.userAgent : "n/a"}
                </div>
              </div>
            )}
          </Card>
        )}
      </div>
    </div>
  );
}
